/**
 * Shared small UPS list helpers — grouping and table cell display (#996 Phase 1).
 * Mirrors rackListUtils.
 *
 * Phase 2.5: the section-agnostic core (grouping, reorder, session patch,
 * duplicate naming, validation, draft, legacy synthesis) now comes from the
 * shared repeatable-section factory. Small-UPS-specific table column display
 * stays here. All public names below are unchanged.
 */

import { DEFAULT_SMALL_UPS_CONFIGURATION } from '../../types/smallUps';
import {
  buildRepeatableNameSubline,
  filterRepeatableLocationTagTableColumns,
  formatRepeatableLocationTagColumnHeader,
  isNativeCompactLayout,
  repeatableNameColumnUsesDoubleLine,
  type RepeatableLocationTagColumnOptions,
} from '../../utils/locationTagListDisplay';
import {
  generateAutoSmallUpsName,
  isAutoGeneratedSmallUpsName,
} from '../../utils/smallUpsAutoName';
import {
  createRepeatableSectionListUtils,
  parseOptionalNumber as parseOptionalNumberCore,
} from '../repeatable/repeatableSectionListUtils';

import type {
  SmallUpsConfiguration,
  GroupedSmallUpsConfigurations,
  UpdateSmallUpsConfigurationInput,
} from '../../types/smallUps';

/** Desktop web columns (#996). MANUFACTURER carries the descriptive copy (model subline). */
export const SMALL_UPS_TABLE_DESKTOP_COLUMNS = [
  'NAME',
  'MANUFACTURER',
  'CAPACITY',
  'LOAD %',
  'PHASE / PLUG',
  'ROOM',
] as const;

/**
 * Compact mirrors desktop column order; LOAD % folds away on narrow screens.
 * Desktop: NAME | MANUFACTURER | CAPACITY | LOAD % | PHASE / PLUG
 * Compact: NAME (+type) | MANUFACTURER (+model) | CAPACITY | PHASE / PLUG (+plug)
 */
export const SMALL_UPS_TABLE_COMPACT_COLUMNS = [
  'NAME',
  'MANUFACTURER',
  'CAPACITY',
  'ROOM',
] as const;

/** All column keys (superset for typing and legacy imports). */
export const SMALL_UPS_TABLE_COLUMNS = SMALL_UPS_TABLE_DESKTOP_COLUMNS;

export type SmallUpsTableLayout = 'desktop' | 'compact';

/** Injected only when `showStatusColumn` is set (Devices view). */
export const SMALL_UPS_TABLE_STATUS_COLUMN = 'STATUS' as const;

export type SmallUpsTableColumnKey =
  | (typeof SMALL_UPS_TABLE_DESKTOP_COLUMNS)[number]
  | (typeof SMALL_UPS_TABLE_COMPACT_COLUMNS)[number]
  | typeof SMALL_UPS_TABLE_STATUS_COLUMN;

export const SMALL_UPS_TABLE_COLUMN_ALIGN: Record<
  SmallUpsTableColumnKey,
  'left' | 'center'
> = {
  NAME: 'left',
  STATUS: 'left',
  MANUFACTURER: 'left',
  CAPACITY: 'center',
  'LOAD %': 'center',
  'PHASE / PLUG': 'center',
  ROOM: 'left',
};

export type SmallUpsListSection = 'existing' | 'new';

// ---------------------------------------------------------------------------
// Section-agnostic core (delegated to the shared repeatable-section factory)
// ---------------------------------------------------------------------------

const core = createRepeatableSectionListUtils<
  SmallUpsConfiguration,
  UpdateSmallUpsConfigurationInput
>({
  defaultConfiguration: DEFAULT_SMALL_UPS_CONFIGURATION,
  generateAutoName: generateAutoSmallUpsName,
  isAutoGeneratedName: isAutoGeneratedSmallUpsName,
  namePrefix: 'UPS ',
  newLabel: 'New small UPS',
  nameNoun: 'Small UPS',
  getDisplayNameFallback: unit => {
    const titled = [unit.smallUpsManufacturer, unit.smallUpsModel]
      .map(part => part?.trim())
      .filter(Boolean)
      .join(' ');
    return titled || undefined;
  },
  legacySourceKeys: [
    'smallUpsManufacturer',
    'smallUpsModel',
    'powerSmallUpsDescription',
    'smallUpsSerialNumber',
    'powerTypesOfUps',
    'smallUpsInputVoltage',
    'smallUpsInputPlug',
    'powerInputPowerPhase',
    'smallUpsCapacity',
    'smallUpsLoadPercentage',
    'powerRuntime',
    'smallUpsBatteryCabinetQty',
    'smallUpsMounting',
    'smallUpsMountType',
    'smallUpsOutlets',
    'smallUpsMaintenanceBypassRequired',
    'smallUpsExpectedLoadGrowth',
    'smallUpsLoadOnGenerator',
    'smallUpsRequiredSensors',
  ],
  mapLegacyFields: source => ({
    smallUpsManufacturer: source.smallUpsManufacturer as string | undefined,
    smallUpsModel: source.smallUpsModel as string | undefined,
    powerSmallUpsDescription: source.powerSmallUpsDescription as
      | string
      | undefined,
    smallUpsSerialNumber: source.smallUpsSerialNumber as string | undefined,
    powerTypesOfUps: source.powerTypesOfUps as string | undefined,
    smallUpsInputVoltage: source.smallUpsInputVoltage as string | undefined,
    smallUpsInputPlug: source.smallUpsInputPlug as string | undefined,
    powerInputPowerPhase: source.powerInputPowerPhase as string | undefined,
    smallUpsCapacity: source.smallUpsCapacity as number | undefined,
    smallUpsLoadPercentage: source.smallUpsLoadPercentage as number | undefined,
    powerRuntime: source.powerRuntime as string | undefined,
    smallUpsBatteryCabinetQty: source.smallUpsBatteryCabinetQty as
      | number
      | undefined,
    smallUpsMounting: source.smallUpsMounting as string | undefined,
    smallUpsMountType: source.smallUpsMountType as string | undefined,
    smallUpsOutlets: source.smallUpsOutlets as string | undefined,
    smallUpsMaintenanceBypassRequired:
      source.smallUpsMaintenanceBypassRequired as boolean | undefined,
    smallUpsExpectedLoadGrowth: source.smallUpsExpectedLoadGrowth as
      | number
      | undefined,
    smallUpsLoadOnGenerator: source.smallUpsLoadOnGenerator as
      | boolean
      | undefined,
    smallUpsRequiredSensors: source.smallUpsRequiredSensors as
      | string
      | undefined,
    smallUpsBackupGenerator: source.smallUpsBackupGenerator as
      | boolean
      | undefined,
    powerSmallUpsAdditionalInfo: source.powerSmallUpsAdditionalInfo as
      | string
      | undefined,
  }),
});

export function normalizeSmallUpsStatus(
  status: SmallUpsConfiguration['status'] | string | undefined,
): SmallUpsConfiguration['status'] {
  return core.normalizeStatus(status);
}

export function groupSmallUpsByStatus(
  units: SmallUpsConfiguration[],
): GroupedSmallUpsConfigurations {
  return core.groupByStatus(units);
}

export function sortSmallUpsByDisplayOrder(
  units: SmallUpsConfiguration[],
): SmallUpsConfiguration[] {
  return core.sortByDisplayOrder(units);
}

/** Reorder units within EXISTING or NEW section; reassigns global displayOrder. */
export function applySmallUpsSectionReorder(
  sortedUnits: SmallUpsConfiguration[],
  section: SmallUpsListSection,
  fromIndex: number,
  toIndex: number,
): SmallUpsConfiguration[] {
  return core.applySectionReorder(sortedUnits, section, fromIndex, toIndex);
}

/** Merge editor save payload onto a session unit without wiping fields with `undefined`. */
export function applySmallUpsSessionPatch(
  unit: SmallUpsConfiguration,
  patch: UpdateSmallUpsConfigurationInput,
): SmallUpsConfiguration {
  return core.applySessionPatch(unit, patch);
}

export function smallUpsIdsEqual(
  left: number | undefined,
  right: number | undefined,
): boolean {
  return core.idsEqual(left, right);
}

export function getSmallUpsManufacturerDisplay(
  unit: SmallUpsConfiguration,
): string {
  return unit.smallUpsManufacturer?.trim() || '—';
}

/** NAME column primary — user-defined name, else "UPS N" by display order. */
export function getSmallUpsNamePrimaryDisplay(
  unit: SmallUpsConfiguration,
): string {
  return core.getNamePrimaryDisplay(unit);
}

/** Editor chrome title — user name, else "UPS N", else "New small UPS". */
export function getSmallUpsEditChromeTitle(
  name: string | undefined,
  options: {
    isNew?: boolean;
    displayOrder?: number;
    positionIndex?: number;
  } = {},
): string {
  return core.getEditChromeTitle(name, options);
}

export function formatSmallUpsTableCell(
  unit: SmallUpsConfiguration,
  column: SmallUpsTableColumnKey,
): string {
  switch (column) {
    case 'NAME':
      return getSmallUpsNamePrimaryDisplay(unit);
    case 'MANUFACTURER':
      return getSmallUpsManufacturerDisplay(unit);
    case 'CAPACITY':
      return unit.smallUpsCapacity != null &&
        Number.isFinite(unit.smallUpsCapacity)
        ? String(unit.smallUpsCapacity)
        : '—';
    case 'LOAD %':
      return unit.smallUpsLoadPercentage != null &&
        Number.isFinite(unit.smallUpsLoadPercentage)
        ? String(unit.smallUpsLoadPercentage)
        : '—';
    case 'PHASE / PLUG':
      return unit.powerInputPowerPhase?.trim() || '—';
    case 'ROOM':
      // Rendered via LocationRoomCell in the row; text unused.
      return '—';
    case 'STATUS':
      // Rendered via RepeatableUnitStatusPill in the row.
      return '';
    default:
      return '—';
  }
}

export function smallUpsTableColumnsForLayout(
  layout: SmallUpsTableLayout,
  options?: RepeatableLocationTagColumnOptions,
): readonly SmallUpsTableColumnKey[] {
  const columns =
    layout === 'compact'
      ? SMALL_UPS_TABLE_COMPACT_COLUMNS
      : SMALL_UPS_TABLE_DESKTOP_COLUMNS;
  return filterRepeatableLocationTagTableColumns(columns, options, layout);
}

export function smallUpsTableHeaderLabel(
  column: SmallUpsTableColumnKey,
  layout: SmallUpsTableLayout,
): string {
  if (layout === 'compact') {
    if (column === 'PHASE / PLUG') {
      return formatRepeatableLocationTagColumnHeader(column, 'PHASE');
    }
    if (column === 'CAPACITY') {
      return formatRepeatableLocationTagColumnHeader(column, 'KVA');
    }
  }
  return formatRepeatableLocationTagColumnHeader(column, column);
}

/** MANUFACTURER subline — model when set. */
export function getSmallUpsModelSubline(
  unit: SmallUpsConfiguration,
): string | undefined {
  const model = unit.smallUpsModel?.trim();
  return model || undefined;
}

/** NAME subline (compact) — type of UPS. */
export function getSmallUpsTypeSubline(
  unit: SmallUpsConfiguration,
): string | undefined {
  const type = unit.powerTypesOfUps?.trim();
  return type || undefined;
}

/** PHASE / PLUG subline — input plug. */
export function getSmallUpsPlugSubline(
  unit: SmallUpsConfiguration,
): string | undefined {
  const plug = unit.smallUpsInputPlug?.trim();
  return plug || undefined;
}

export function getSmallUpsColumnSubline(
  column: SmallUpsTableColumnKey,
  unit: SmallUpsConfiguration,
  layout: SmallUpsTableLayout,
  options?: { locationTagName?: string | null },
): string | undefined {
  // Native compact (#999 Option B): STATUS pill folds under NAME, the type fold
  // relocates to the trailing CAPACITY cell, and MANUFACTURER carries a `Model:`
  // label.
  if (isNativeCompactLayout(layout)) {
    if (column === 'MANUFACTURER') {
      const model = unit.smallUpsModel?.trim();
      return model ? `Model: ${model}` : undefined;
    }
    if (column === 'CAPACITY') {
      return getSmallUpsTypeSubline(unit);
    }
    return undefined;
  }
  if (column === 'NAME') {
    return buildRepeatableNameSubline(layout, {
      compactParts:
        layout === 'compact' ? [getSmallUpsTypeSubline(unit)] : undefined,
      locationTagName: options?.locationTagName,
    });
  }
  if (column === 'MANUFACTURER') {
    return getSmallUpsModelSubline(unit);
  }
  if (column === 'PHASE / PLUG') {
    return getSmallUpsPlugSubline(unit);
  }
  return undefined;
}

export function smallUpsColumnUsesDoubleLine(
  column: SmallUpsTableColumnKey,
  layout: SmallUpsTableLayout = 'desktop',
  options?: { locationTagName?: string | null },
): boolean {
  if (isNativeCompactLayout(layout)) {
    // NAME is single-line (pill renders beneath it in the row); MANUFACTURER
    // (middle) and CAPACITY (trailing) double-line their relocated sublines.
    return column === 'MANUFACTURER' || column === 'CAPACITY';
  }
  if (column === 'NAME') {
    return repeatableNameColumnUsesDoubleLine(layout, options?.locationTagName);
  }
  return column === 'MANUFACTURER' || column === 'PHASE / PLUG';
}

/**
 * Primary list title — user-defined name preferred, then manufacturer + model,
 * then "UPS N" when displayOrder is available. Returns null when no suitable name.
 */
export function getSmallUpsDisplayName(
  unit: SmallUpsConfiguration,
): string | null {
  return core.getDisplayName(unit);
}

/** Unique duplicate name — user names get `-copy`; unnamed/auto names get a fresh serial. */
export function buildDuplicateSmallUpsName(
  sourceName: string | undefined,
  takenNames: Iterable<string | undefined> = [],
  tempId: number,
): string {
  return core.buildDuplicateName(sourceName, takenNames, tempId);
}

/** Assign a serial name when the user did not provide one. */
export function ensureSmallUpsNameForCreate(
  data: UpdateSmallUpsConfigurationInput,
  tempId: number,
  takenNames: Iterable<string | undefined> = [],
): UpdateSmallUpsConfigurationInput {
  return core.ensureNameForCreate(data, tempId, takenNames);
}

/** Client-side uniqueness check before persisting small UPS session changes. */
export function validateSessionSmallUpsNames(
  session: SmallUpsConfiguration[],
): string | null {
  return core.validateSessionNames(session);
}

/** Clone fields for duplicate flow (no id/metadata; keeps source existing/new status). */
export function cloneSmallUpsForCreate(
  source: SmallUpsConfiguration,
  takenNames: Iterable<string | undefined> = [],
  tempId: number,
): Partial<SmallUpsConfiguration> {
  return core.cloneForCreate(source, takenNames, tempId);
}

/** Parse numeric form input — returns undefined for empty or non-finite values. */
export const parseOptionalNumber = parseOptionalNumberCore;

export function createDraftSmallUpsConfiguration(
  data: UpdateSmallUpsConfigurationInput,
  displayOrder: number,
  tempId: number,
): SmallUpsConfiguration {
  return core.createDraftConfiguration(data, displayOrder, tempId);
}

/** Client-only id for a unit synthesized from legacy flat request columns. */
export const LEGACY_SMALL_UPS_SYNTHETIC_ID = core.SYNTHETIC_ID;

/** Whether a list row may offer delete (draft rows or persisted id > 0, not legacy synthetic). */
export function canDeleteSmallUpsFromList(
  unit: SmallUpsConfiguration,
  isDraftMode: boolean,
): boolean {
  return core.canDeleteFromList(unit, isDraftMode);
}

export function hasLegacySmallUpsRequestFields(
  source: Record<string, unknown>,
): boolean {
  return core.hasLegacyRequestFields(source);
}

/** Build one display row from pre–multi-instance flat request fields when child rows are absent. */
export function buildLegacySmallUpsFromRequestFields(
  source: Record<string, unknown>,
  requestId: string | number,
): SmallUpsConfiguration | null {
  return core.buildLegacyFromRequestFields(source, requestId);
}
